import { useEffect } from 'react';
import { css } from '@emotion/react';

import { Step } from '@widget/core';
import { useTheme } from '@toolbox/style';
import { HiddenField, QuestionLayout } from '../../components';
import { QuestionStepOptionListV3 } from './QuestionStepOptionListV3';
import { AssistedGuidanceModal } from './AssistedGuidanceModal';

import type { Guidance } from './AssistedGuidanceModal';
import type { QuestionLayoutProps, RadioCardProps } from '../../components';
import type { QuestionStepSchema } from '../../storyblok/schemas';
import type { StepProps } from '@widget/core';
import type { z } from 'zod';
import type { ReactElement } from 'react';

export type QuestionStepProps = StepProps &
  Omit<QuestionLayoutProps, 'submit'> & {
    name: string;
    gridSize?: number;
    valueType?: string;
    modalCta?: string;
    modalTitle?: string;
    modalGridSize?: number;
    includeGuidanceModal?: boolean;
    guidances?: Guidance[];
  } & Pick<z.infer<typeof QuestionStepSchema>, 'options' | 'hiddenFields' | 'showUSPList'>;

export type QuestionStepOptionListProps = {
  name: string;
  gridSize?: number | null;
  valueType?: string;
  optionList: RadioCardProps[];
};

export const QuestionStep = ({
  id,
  nextStep,
  back,
  title,
  subTitle,
  description,
  backButtonText,
  submitButtonText,
  skipButtonText,
  closeButtonText,
  options,
  hiddenFields,
  name,
  gridSize,
  valueType,
  includeGuidanceModal,
  modalCta,
  modalTitle,
  modalGridSize,
  guidances,
  headerType,
  socialProofMessage,
  analyticsMap,
  showProgressBar,
}: QuestionStepProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const theme = useTheme();

  const imgCss = css`
    height: 30px;
    width: auto;
    margin-right: ${theme.spacing(4)};
    font-size: ${theme.iconSizes.sm}px;

    ${theme.media.up('sm')} {
      font-size: ${theme.iconSizes.md}px;
    }
  `;

  const HiddenFieldComps = hiddenFields?.map((props) => (
    <HiddenField
      key={props._uid}
      type={props.type}
      name={props.name || 'placeholder'}
      storyBlokValue={props.value || ''}
    />
  ));

  const optionList = options.map(
    ({ label, value, image, imageHover, icon, nextStep, redirectUrl }) => {
      const iconItem =
        typeof image === 'object' && image?.filename
          ? ((<img css={imgCss} src={image.filename} alt={image.alt || ''} />) as ReactElement)
          : icon;

      const iconItemHover =
        typeof imageHover === 'object' && imageHover?.filename
          ? ((
              <img css={imgCss} src={imageHover.filename} alt={imageHover.alt || ''} />
            ) as ReactElement)
          : icon;

      return {
        label,
        value,
        icon: iconItem,
        iconHover: iconItemHover,
        nextStep: nextStep,
        redirectUrl,
      };
    },
  );

  return (
    <Step
      id={id}
      nextStep={nextStep}
      headerType={headerType}
      socialProofMessage={socialProofMessage}
      analyticsMap={analyticsMap}
    >
      <QuestionLayout
        back={back}
        title={title}
        subTitle={subTitle}
        description={description}
        backButtonText={backButtonText}
        submitButtonText={submitButtonText}
        skipButtonText={skipButtonText}
        closeButtonText={closeButtonText}
        headerType={headerType}
        socialProofMessage={socialProofMessage}
        showProgressBar={showProgressBar}
      >
        {HiddenFieldComps}
        <QuestionStepOptionListV3
          gridSize={gridSize}
          valueType={valueType}
          name={name}
          optionList={optionList}
        />
        {includeGuidanceModal && (
          <AssistedGuidanceModal
            modalCta={modalCta}
            modalTitle={modalTitle}
            gridSize={modalGridSize}
            guidances={guidances}
          />
        )}
      </QuestionLayout>
    </Step>
  );
};
